import React from 'react'
import * as  THREE from 'three'
import { useEffect, useRef } from 'react'
import { useGLTF, useAnimations, Html, Text } from '@react-three/drei'
import { useControls } from 'leva'
import { useFrame } from '@react-three/fiber'


export default function Model() {
  const model = useGLTF('/keyboard818.glb')
  const keyboardRef = useRef(null)
  const actions = useAnimations(model.animations, model.scene)
  const audioCache = useRef({})

  // 保存每个 mesh 的目标 position
  const meshTargetPositions = useRef({})
  const textareaRef = useRef(null)

  const playSound = (keyCode, isPress) => {
    // 根据按键代码映射到对应的音效文件
    const soundMap = {
      'KeyDP': 'D_press.mp3',
      'KeyHP': 'H_press.mp3',
      'KeyLP': 'L_press.mp3',
      'EnterP': 'enter_press.mp3',
      'ShiftLeftP': 'shift_press.mp3',
      'ShiftRightP': 'shift_press.mp3',
      'SpaceP': 'space_press.mp3',
      'BackspaceP': 'backspace_press.mp3',
      'OptionP': 'option_press.mp3',
      'KeyDU': 'D_up.mp3',
      'KeyHU': 'H_up.mp3',
      'KeyLU': 'L_up.mp3',
      'EnterU': 'enter_up.mp3',
      'ShiftLeftU': 'shift_up.mp3',
      'ShiftRightU': 'shift_up.mp3',
      'SpaceU': 'space_up.mp3',
      'BackspaceU': 'backspace_up.mp3',
      'OptionU': 'option_up.mp3',
    }
    function getSoundEffectName(e) {
      const FKeyRex = /^F(1[0-2]|[1-9])$/
      const DigitRex = /^Digit(1[0-2]|[1-9])$/
      if (e.includes('Key')) return 'KeyL'
      if (FKeyRex.test(e)) return 'KeyH'
      if (DigitRex.test(e)) return 'KeyL'
      if (e.includes('Enter')) return 'Enter'
      if (e.includes('ShiftLeft')) return 'ShiftLeft'
      if (e.includes('ShiftRight')) return 'ShiftRight'
      if (e.includes('Space')) return 'Space'
      if (e.includes('Backspace')) return 'Backspace'
      return 'Option'
    }

    const soundFile = soundMap[getSoundEffectName(keyCode) + (isPress ? 'P' : 'U')]
    if (soundFile && !audioCache.current[keyCode]) {
      // 创建新的Audio对象并缓存
      const audio = new Audio(`/soundEffect/${soundFile}`)
      audio.volume = 1 // 设置音量
      audioCache.current[keyCode] = audio
    }

    // 播放音效
    if (audioCache.current[keyCode]) {
      const audio = audioCache.current[keyCode]
      audio.currentTime = 0 // 重置播放位置
      audio.play().catch(e => console.log('音效播放失败:', e))
    }
  }

  // 初始化所有 mesh 的目标 position
  useEffect(() => {
    Object.keys(model.meshes).forEach((meshName) => {
      const mesh = model.meshes[meshName]
      meshTargetPositions.current[meshName] = mesh.position.toArray()
    })
  }, [model])

  useFrame(() => {
    const speed = 0.2
    Object.entries(model.meshes).forEach(([meshName, mesh]) => {
      const target = meshTargetPositions.current[meshName]
      if (target) {
        mesh.position.lerp(new THREE.Vector3(...target), speed)
      }
    })
  })

  useEffect(() => {
    const handleKeydown = (e) => {
      textareaRef.current.focus()
      // e.preventDefault() // 阻止默认行为
      const mesh = model.meshes[e.code]
      if (meshTargetPositions.current[e.code][1] === 0) playSound(e.code, true)
      if (mesh) {
        // 向下移动
        meshTargetPositions.current[e.code][1] = -0.044
      }
    }


    const handleKeyup = (e) => {
      playSound(e.code, false)
      const mesh = model.meshes[e.code]
      if (mesh) {
        // 恢复原位
        meshTargetPositions.current[e.code][1] = 0
      }
    }

    window.addEventListener('keydown', handleKeydown)
    window.addEventListener('keyup', handleKeyup)
    return () => {
      window.removeEventListener('keydown', handleKeydown)
      window.removeEventListener('keyup', handleKeyup)
    }
  }, [model])


  const handlePointerDown = (e) => {
    e.stopPropagation()
    const meshWhiteList = ['Case', 'Keyboard_cable']
    if (meshWhiteList.includes(e.object.name)) return
    playSound(e.object.name, true)
    meshTargetPositions.current[e.object.name][1] = -0.044
  }

  const handlePointerUp = (e) => {
    e.stopPropagation()
    if (e.object.name.includes('Key')) textareaRef.current.value += e.object.name.replace('Key', '').toLowerCase()
    if (e.object.name === 'Space') textareaRef.current.value += ' '
    if (e.object.name === 'Backspace') textareaRef.current.value = textareaRef.current.value.slice(0, -1)
    if (e.object.name === 'Enter') textareaRef.current.value += '\n'
    if (textareaRef.current) textareaRef.current.focus()
    playSound(e.object.name, false)
    meshTargetPositions.current[e.object.name][1] = 0
  }

  const handlePointerOut = (e) => {
    e.stopPropagation()
    meshTargetPositions.current[e.object.name][1] = 0
  }

  return <>

    <primitive
      position={[0, -0.5, -2]}
      ref={keyboardRef}
      object={model.scene}
      scale={2}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerOut={handlePointerOut}
      rotation={[0, -0.2, 0]}
    >
      <Html
        transform
        wrapperClass="html-wrapper"
        distanceFactor={1.17}
        position={[0.18, 1, 0.4]}
      >
        <textarea
          className='textarea'
          ref={textareaRef}
          autoFocus
          rows={3}
          placeholder='Type here...'
        />
      </Html>
    </primitive>
  </>
}

useGLTF.preload('/hamburger.glb')
